import * as React from "react";
import { apiFetch, startProactiveRefresh, stopProactiveRefresh } from "@/lib/api.js";
import type { OnboardingGoalOption } from "@homethyst/shared/schemas";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  photoUrl: string | null;
  householdId: string;
  householdName: string;
  role: string;
  plan: string;
  emailVerified: boolean;
  isAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, goals?: OnboardingGoalOption[], stressSpaces?: { room: string; reason: string }[]) => Promise<void>;
  loginWithGoogle: (idToken: string, goals?: OnboardingGoalOption[], stressSpaces?: { room: string; reason: string }[]) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    try {
      const data = await apiFetch("/api/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  React.useEffect(() => {
    // Try cached user first to avoid a request on every mount
    const cached = sessionStorage.getItem("ht_user");
    if (cached) {
      try { setUser(JSON.parse(cached)); } catch {}
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Sync user to sessionStorage for fast initial render
  React.useEffect(() => {
    if (user) sessionStorage.setItem("ht_user", JSON.stringify(user));
    else sessionStorage.removeItem("ht_user");
  }, [user]);

  // Keep the session alive in the background while authenticated.
  React.useEffect(() => {
    if (user) startProactiveRefresh();
    else stopProactiveRefresh();
    return () => stopProactiveRefresh();
  }, [user]);

  const login = async (email: string, password: string) => {
    const data = await apiFetch("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    localStorage.setItem("ht_onboarding_seen", "1");
    setUser(data);
  };

  const register = async (email: string, password: string, goals?: OnboardingGoalOption[], stressSpaces?: { room: string; reason: string }[]) => {
    const data = await apiFetch("/api/auth/register", { method: "POST", body: JSON.stringify({ email, password, goals, stressSpaces: stressSpaces?.length ? stressSpaces : undefined }) });
    localStorage.setItem("ht_onboarding_seen", "1");
    setUser(data);
  };

  const loginWithGoogle = async (idToken: string, goals?: OnboardingGoalOption[], stressSpaces?: { room: string; reason: string }[]) => {
    const data = await apiFetch("/api/auth/google", { method: "POST", body: JSON.stringify({ idToken, goals, stressSpaces: stressSpaces?.length ? stressSpaces : undefined }) });
    localStorage.setItem("ht_onboarding_seen", "1");
    setUser(data);
  };

  const logout = async () => {
    await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
